import { useContext, useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import ColM from "../../assets/components/column/ColM";
import createOrderContext from "../../context/createOrder";
import { usePrices } from "../../context/prices/PricesContext";
import { Material } from "../../types/orderTypes";
import RegelBedrag, { RegelAantal } from "./RegelBedrag";

/**
 * Extra's: LED (met de opties dimmer/afstandsbediening en bewegingssensor) en
 * plateau.
 *
 * Deze component legt alleen keuzes vast. De bedragen die ernaast staan komen
 * uit de calculatie van de backend.
 *
 * Volfineer is geen keuze meer: bij een open trap zit het er altijd in (één vel
 * per 2 treden), bij een dichte trap is het niet van toepassing. Bij een open
 * trap staat het hieronder ter informatie.
 */
const Extras = () => {
  const {
    extrasLed,
    plateauMaterial,
    extrasPlateau,
    ledMode,
    ledCustomText,
    ledDimmer,
    ledSensor,
    plateauSize,
    type,
  } = useContext(createOrderContext);

  const { getMaterialName, plateauSizes, materials } = usePrices();

  // LED is niet mogelijk bij een open trap: de optie wordt verborgen en een
  // eerder gemaakte keuze wordt teruggezet, zodat de configuratie die naar de
  // server gaat geen LED bevat (die zou hem anders afkeuren).
  const isOpenStairs = type.value === "Open";

  useEffect(() => {
    if (isOpenStairs && extrasLed.value) {
      extrasLed.set(false);
      ledMode.set("");
      ledCustomText.set("");
      ledDimmer.set(false);
      ledSensor.set(false);
    }
    // eslint-disable-next-line
  }, [isOpenStairs, extrasLed.value]);

  // Zonder gekozen maat kan de backend niets rekenen; kies de eerste.
  useEffect(() => {
    if (!extrasPlateau.value) {
      plateauSize.set("");
      return;
    }
    if (!plateauSize.value && plateauSizes.length > 0) {
      plateauSize.set(plateauSizes[0]?.label ?? "");
    }
    // eslint-disable-next-line
  }, [extrasPlateau.value, plateauSizes.length]);

  return (
    <>
      {!isOpenStairs && (
        <ColM xs={12}>
          <Row>
            <Form.Group as={Col} xs={6} md={4}>
              <Form.Label>Ledverlichting</Form.Label>

              <select
                className="select"
                style={{ padding: "8px 4px", position: "inherit" }}
                value={extrasLed.value ? "1" : "0"}
                onChange={(event) => {
                  if (event.target.value === "1") {
                    extrasLed.set(true);
                    if (!ledMode.value) ledMode.set("ELKE_TREDE");
                  } else {
                    extrasLed.set(false);
                    ledMode.set("");
                    ledCustomText.set("");
                    ledDimmer.set(false);
                    ledSensor.set(false);
                  }
                }}
              >
                <option value={"1"} className="select-item">
                  Ja
                </option>
                <option value={"0"} className="select-item">
                  Nee
                </option>
              </select>
            </Form.Group>

            {/* De hele LED-post: sets, toeslag, trafo, kabelset en opties. */}
            <RegelBedrag
              code={["led", "led-toeslag", "led-trafo", "led-kabelset", "led-dimmer", "led-sensor"]}
              sizes={{ xs: 6, md: 4 }}
            />
          </Row>

          {extrasLed.value && (
            <Row className="mt-2">
              <Col xs={12}>
                <Form.Check
                  inline
                  type="radio"
                  name="ledMode"
                  id="led-elke-trede"
                  label="Elke trede"
                  checked={ledMode.value === "ELKE_TREDE"}
                  onChange={() => ledMode.set("ELKE_TREDE")}
                />
                <Form.Check
                  inline
                  type="radio"
                  name="ledMode"
                  id="led-om-en-om"
                  label="Om en om"
                  checked={ledMode.value === "OM_EN_OM"}
                  onChange={() => ledMode.set("OM_EN_OM")}
                />
                <Form.Check
                  inline
                  type="radio"
                  name="ledMode"
                  id="led-anders"
                  label="Anders, namelijk:"
                  checked={ledMode.value === "ANDERS"}
                  onChange={() => ledMode.set("ANDERS")}
                />
                {ledMode.value === "ANDERS" && (
                  <Form.Control
                    type="text"
                    className="mt-2"
                    placeholder="Omschrijf de gewenste ledverlichting"
                    value={ledCustomText.value}
                    onChange={(e) => ledCustomText.set(e.target.value)}
                  />
                )}
              </Col>
            </Row>
          )}

          {extrasLed.value && (
            <Row className="mt-2">
              <Col xs={12}>
                <Form.Check
                  inline
                  type="checkbox"
                  id="led-dimmer"
                  label="Dimmer / afstandsbediening"
                  checked={ledDimmer.value}
                  onChange={(e) => ledDimmer.set(e.target.checked)}
                />
                <Form.Check
                  inline
                  type="checkbox"
                  id="led-sensor"
                  label="Bewegingssensor"
                  checked={ledSensor.value}
                  onChange={(e) => ledSensor.set(e.target.checked)}
                />
              </Col>
            </Row>
          )}
        </ColM>
      )}

      <ColM xs={12}>
        <Row className="mt-2">
          <Form.Group as={Col} xs={3}>
            <Form.Label>Plateau</Form.Label>

            <select
              className="select"
              style={{ padding: "8px 4px", position: "inherit" }}
              value={extrasPlateau.value ? "1" : "0"}
              onChange={(event) => {
                extrasPlateau.set(event.target.value === "1");
              }}
            >
              <option value={"1"} className="select-item">
                Ja
              </option>
              <option value={"0"} className="select-item">
                Nee
              </option>
            </select>
          </Form.Group>

          <Form.Group as={Col} xs={3}>
            <Form.Label>Plateau Materiaal</Form.Label>

            {extrasPlateau.value ? (
              <select
                className="select"
                style={{ padding: "8px 4px", position: "inherit" }}
                value={plateauMaterial.value}
                onChange={(event) => {
                  plateauMaterial.set(event.target.value as Material);
                }}
              >
                {materials.map((item: any) => (
                  <option value={item} key={item} className="select-item">
                    {getMaterialName(item)}
                  </option>
                ))}
              </select>
            ) : (
              <Form.Control type={"text"} disabled={true} value={"Geen plateau"} />
            )}
          </Form.Group>

          <Form.Group as={Col} xs={3}>
            <Form.Label>Plateau maat (cm)</Form.Label>

            {extrasPlateau.value ? (
              <select
                className="select"
                style={{ padding: "8px 4px", position: "inherit" }}
                value={plateauSize.value}
                onChange={(event) => {
                  plateauSize.set(event.target.value);
                }}
              >
                {plateauSizes.map((size) => (
                  <option value={size.label} key={size.id} className="select-item">
                    {size.label}
                  </option>
                ))}
              </select>
            ) : (
              <Form.Control type={"text"} disabled={true} value={"—"} />
            )}
          </Form.Group>

          <RegelBedrag code="plateau" sizes={{ xs: 3 }} />
        </Row>
      </ColM>

      {isOpenStairs && (
        <ColM xs={12}>
          <Row className="mt-2">
            <Form.Group as={Col} xs={12} md={4}>
              <Form.Label>Volfineer onderkant</Form.Label>
              <Form.Control
                type="text"
                disabled
                value="Inbegrepen bij een open trap"
              />
            </Form.Group>

            <RegelAantal code="volfineer" label="Vellen (1 per 2 treden)" sizes={{ xs: 6, md: 4 }} />
            <RegelBedrag code="volfineer" sizes={{ xs: 6, md: 4 }} />
          </Row>
        </ColM>
      )}
    </>
  );
};

export default Extras;
